import { createFileRoute } from '@tanstack/react-router'
import { todayIso } from '@/lib/today'
import { hhmm, timeLabel } from '@/lib/zoned-time'
import { useState } from 'react'
import { Plus, Repeat, Pencil, Trash2, Pause, Play } from 'lucide-react'
import { toast } from 'sonner'
import { AddressPicker, type AddressValue } from '@/components/address-picker'
import { useMaintenancePlans, FREQUENCIES, DURATIONS, freqLabel, type MaintenancePlan } from '@/hooks/use-maintenance-plans'
import { useClients, clientName } from '@/hooks/use-clients'
import { useTeam } from '@/hooks/use-company'
import { Page, PageHeader, Card, btnPrimary, btnGhost, hoverActions, StatusPill, EmptyState, SkeletonRows } from '@/components/rm'
import { Modal, Field, Input, Select, Textarea } from '@/components/rm/form'
import { phFormat, phDay } from '@/lib/ph'

export const Route = createFileRoute('/_app/maintenance')({ component: MaintenancePage })

const fmtDate = (d: string | null) => (d ? phFormat(phDay(d), 'date', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

type Draft = Partial<MaintenancePlan> & { freqKey?: string }
const BLANK: Draft = { name: '', contact_id: '', freqKey: '1-month', next_run_date: todayIso(), start_time: '08:00', duration_minutes: 60, lead_days: 7, active: true, client_comms_mode: 'none' }

function MaintenancePage() {
  const { plans, isLoading, save, setActive, remove } = useMaintenancePlans()
  const { clients } = useClients()
  const { members } = useTeam()
  const [editing, setEditing] = useState<Draft | null>(null)

  const openNew = () => setEditing({ ...BLANK })
  const openEdit = (p: MaintenancePlan) => setEditing({ ...p, freqKey: `${p.interval_count}-${p.interval_unit}` })

  const submit = async () => {
    if (!editing?.name?.trim()) return toast.error('Give the plan a name')
    if (!editing.next_run_date) return toast.error('Set the next due date')
    if (!hhmm(editing.start_time)) return toast.error('Set a start time')
    const [count, unit] = (editing.freqKey ?? '1-month').split('-')
    try {
      await save.mutateAsync({
        id: editing.id, name: editing.name, contact_id: editing.contact_id || null,
        interval_count: Number(count), interval_unit: unit as 'week' | 'month',
        next_run_date: editing.next_run_date, end_date: editing.end_date || null,
        start_time: hhmm(editing.start_time), duration_minutes: editing.duration_minutes ?? 60,
        lead_days: editing.lead_days ?? 7, default_user_id: editing.default_user_id || null,
        address: editing.address || null, notes: editing.notes || null, active: editing.active ?? true,
        client_comms_mode: editing.client_comms_mode ?? 'none', quote_amount: editing.quote_amount ?? null,
      })
      toast.success('Plan saved'); setEditing(null)
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  const memberName = (id: string | null | undefined) => {
    const m = members.find((x) => x.id === id)
    return m ? [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email : 'Unassigned'
  }

  return (
    <Page>
      <PageHeader title="Recurring & maintenance" subtitle="Contracts that auto-create jobs on a schedule."
        actions={<button className={btnPrimary} onClick={openNew}><Plus className="h-4 w-4" /> New plan</button>} />

      <Card className="mt-5">
        {isLoading ? <SkeletonRows /> : plans.length === 0 ? (
          <EmptyState label="No maintenance plans yet. Set up a recurring contract and jobs get created automatically." cta="New plan" onCta={openNew} />
        ) : (
          <div className="divide-y divide-token-subtle">
            {plans.map((p) => (
              <div key={p.id} className="group flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 hover:bg-token-surface sm:px-5 sm:py-3.5">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-token-selected text-token-warning"><Repeat className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-semibold text-token-primary">{p.name}</p>
                  <p className="truncate text-[12.5px] text-token-secondary">
                    {p.contact ? (clientName(p.contact) || 'Client') : 'No client'} · {freqLabel(p.interval_count, p.interval_unit)} · {memberName(p.default_user_id)}
                  </p>
                </div>
                {/* On phones this block wraps to its own line under the name and spreads full-width; on ≥sm it sits inline on the right. */}
                <div className="flex w-full items-center gap-3 pl-12 sm:w-auto sm:pl-0">
                  <div className="text-left sm:text-right">
                    <p className="text-[12px] text-token-secondary">Next due</p>
                    <p className="text-[13.5px] font-bold text-token-primary">{fmtDate(p.next_run_date)}</p>
                    {/* The time is on the row, not just in the form: a plan booking itself at the
                        wrong hour was invisible here until somebody opened the calendar. */}
                    <p className="text-[12px] text-token-secondary">{timeLabel(p.start_time)}</p>
                  </div>
                  <StatusPill status={p.active ? 'active' : 'paused'} label={p.active ? 'Active' : 'Paused'} />
                  <div className={`ml-auto flex items-center gap-1 sm:ml-0 ${hoverActions}`}>
                    <button className={btnGhost} title={p.active ? 'Pause' : 'Resume'} aria-label={p.active ? 'Pause plan' : 'Resume plan'} onClick={() => setActive.mutate({ id: p.id, active: !p.active })}>{p.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</button>
                    <button className={btnGhost} aria-label="Edit plan" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></button>
                    <button className={btnGhost} aria-label="Delete plan" onClick={() => { if (confirm('Delete this plan? Existing jobs are kept.')) remove.mutate(p.id) }}><Trash2 className="h-4 w-4 text-rose-400" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Edit plan' : 'New maintenance plan'}
        footer={<>
          <button className={btnGhost} onClick={() => setEditing(null)}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save plan'}</button>
        </>}
      >
        {editing && (
          <div className="space-y-4">
            <Field label="Plan name"><Input value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Quarterly pest treatment" autoFocus /></Field>
            <Field label="Client">
              <Select value={editing.contact_id ?? ''} onChange={(e) => setEditing({ ...editing, contact_id: e.target.value })}>
                <option value="">No client</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Frequency">
                <Select value={editing.freqKey ?? '1-month'} onChange={(e) => setEditing({ ...editing, freqKey: e.target.value })}>
                  {FREQUENCIES.map((f) => <option key={`${f.count}-${f.unit}`} value={`${f.count}-${f.unit}`}>{f.label}</option>)}
                </Select>
              </Field>
              <Field label="Next due date"><Input type="date" value={editing.next_run_date ?? ''} onChange={(e) => setEditing({ ...editing, next_run_date: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Start time"><Input type="time" value={hhmm(editing.start_time)} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} /></Field>
              <Field label="How long">
                <Select value={String(editing.duration_minutes ?? 60)} onChange={(e) => setEditing({ ...editing, duration_minutes: Number(e.target.value) })}>
                  {DURATIONS.map((d) => <option key={d.minutes} value={d.minutes}>{d.label}</option>)}
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Default assignee">
                <Select value={editing.default_user_id ?? ''} onChange={(e) => setEditing({ ...editing, default_user_id: e.target.value })}>
                  <option value="">Unassigned</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}</option>)}
                </Select>
              </Field>
              <Field label="Create job (days before due)"><Input type="number" min={0} value={editing.lead_days ?? 7} onChange={(e) => setEditing({ ...editing, lead_days: e.target.valueAsNumber || 0 })} /></Field>
            </div>
            <Field label="End date (optional)"><Input type="date" value={editing.end_date ?? ''} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} /></Field>
            <AddressPicker
              label="Job address (optional — else uses client address)"
              parts={false}
              value={{ address: editing.address ?? null, line1: null, line2: null, city: null, state: null, postal_code: null, manual_address: false }}
              onChange={(a: AddressValue) => setEditing({ ...editing, address: a.address })}
            />
            <Field label="Notify the client">
              <Select value={editing.client_comms_mode ?? 'none'} onChange={(e) => setEditing({ ...editing, client_comms_mode: e.target.value })}>
                <option value="none">Don't email the client</option>
                <option value="reminder">Send a reminder email</option>
                <option value="quote">Send a quote to accept</option>
              </Select>
            </Field>
            {editing.client_comms_mode === 'quote' && (
              <Field label="Quote amount (ex VAT)"><Input type="number" min={0} step="0.01" value={editing.quote_amount ?? 0} onChange={(e) => setEditing({ ...editing, quote_amount: e.target.valueAsNumber || 0 })} /></Field>
            )}
            {editing.client_comms_mode !== 'none' && (
              <p className="text-[12px] text-token-secondary">{editing.client_comms_mode === 'quote' ? 'Each time a job is generated, the client is emailed a quote at this price to accept online.' : 'Each time a job is generated, the client gets a reminder email with the date.'} Sent when the job is created ({editing.lead_days ?? 7} days before due).</p>
            )}
            <Field label="Notes"><Textarea rows={2} value={editing.notes ?? ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
          </div>
        )}
      </Modal>
    </Page>
  )
}
